/**
 * A unified SmartTable component that consolidates all duplicated table implementations
 */

import React, { useState } from "react";
import clsx from "clsx";
import MenuList from "@material-ui/core/MenuList";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import IconButton from "@material-ui/core/IconButton";
import Radio from "@material-ui/core/Radio";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import DeleteOutlinedIcon from "@material-ui/icons/DeleteOutlined";
import { FormattedMessage } from "react-intl";
import SortableArrow from "./SortableArrow";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import parseAndFormat from "../texts/parseAndFormat";
import parseToFloat from "../texts/parseToFloat";
import parseData from "../texts/parseData";
import {usePrevAllocatedStyles} from "../../common/styles/allocationStyles";
import { brandColors } from "../../theme";
import {
  StyledHeadMenu,
  StyledRowMenu,
  StyledMenuItem,
  StyledTableCell,
  StyledTableRow,
  Order,
  desc,
  stableSort,
  getSorting,
} from "../shared/StyledComponents";
import moment from "moment";

const parseDataDecimal = ["eec", "el", "ep", "etd", "eu", "esca", "eccs", "eccr", "eee", "etot"];
const parseDataDate = ["b_l_date", "site_start", "pos_date"];

const useStyles = usePrevAllocatedStyles;

interface headCell {
  name?: string | React.ReactElement;
  idFormattedName?: string;
  id: string;
  alignRight?: boolean;
  size?: string;
}

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  order: Order;
  orderBy: string;
  headCells: headCell[];
  filteredHead: any;
}

interface EnhancedTableRowComponentProps {
  row: any;
  headCells: headCell[];
  classes: ReturnType<typeof useStyles>;
  filteredHead: any;
  smartProps: any;
  editableProps: any;
  open: any;
  setOpen: any;
  backgroundColor?: any;
}

type EnhancedTableRowComponentType = React.ComponentType<EnhancedTableRowComponentProps>;

interface SmartTableConfig {
  useForStorage?: boolean;
  useForPrevTable?: boolean;
  useForAllocation?: boolean;
}

interface ISmartTableProps {
  headCells: headCell[];
  rows: any;
  smartProps?: any;
  editableProps?: any;
  whiteHeader?: boolean;
  verticalOverflow?: boolean;
  aggregate?: {
    removeZeroBalanced?: boolean;
    showZeroBalanced?: boolean;
    required: string[];
    addUp: string[];
  };
  config?: SmartTableConfig;
}

// common function
function EnhancedTableHead(
  props: EnhancedTableProps & { headHeight: string; backgroundColor: string }
) {
  const {
    backgroundColor,
    headHeight,
    classes,
    order,
    orderBy,
    headCells,
    onRequestSort,
    filteredHead,
  } = props;
  const createSortHandler =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell, index) =>
          filteredHead[headCell.id] ? null : (
            <StyledTableCell
              key={headCell.id + "headCell" + index}
              sortDirection={orderBy === headCell.id ? order : false}
              align={headCell.alignRight ? "right" : "left"}
              style={{
                backgroundColor,
                height: headHeight,
                width: headCell.size,
              }}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={order}
                onClick={createSortHandler(headCell.id)}
                IconComponent={props => (
                  <SortableArrow
                    active={orderBy === headCell.id}
                    order={order}
                    {...props}
                  ></SortableArrow>
                )}
              >
                <Typography className={classes.columnTitle}>
                  {headCell.idFormattedName ? (
                    <FormattedMessage id={headCell.idFormattedName} />
                  ) : (
                    headCell.name
                  )}
                </Typography>
                {orderBy === headCell.id ? (
                  <span className={classes.visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </span>
                ) : null}
              </TableSortLabel>
            </StyledTableCell>
          )
        )}
      </TableRow>
    </TableHead>
  );
}

function EnhancedTableRowStorage(props: {
  row: any;
  headCells: headCell[];
  classes: ReturnType<typeof useStyles>;
  filteredHead: any;
  smartProps: any;
  editableProps: any;
  open: any;
  setOpen: any;
  backgroundColor?: any;
}) {
  const {
    row,
    headCells,
    classes,
    filteredHead,
    smartProps,
    editableProps,
    open,
    setOpen,
    backgroundColor,
  } = props;

  return (
      <StyledTableRow
          style={{
            backgroundColor,
          }}
      >
        {headCells.map((headCell, i) =>
            filteredHead[headCell.id] ? null : (
                <StyledTableCell
                    align={headCell.alignRight ? "right" : "left"}
                    key={headCell.id + row.name + i}
                    style={{
                      whiteSpace: smartProps ? "nowrap" : "break-spaces",
                    }}
                >
                  {i === 0 && row.child ? (
                      <KeyboardArrowDownIcon
                          color="primary"
                          className={classes.toggleIcon}
                          style={{
                            transform: open ? "rotate(180deg)" : "rotate(0deg)",
                            color: open ? brandColors.primary.main : "#fff",
                            backgroundColor: open ? "#fff" : brandColors.primary.main,
                          }}
                          fontSize="small"
                          onClick={() => setOpen(!open)}
                      />
                  ) : editableProps && headCell.id === editableProps.id ? (
                      <TextField
                          className={
                            editableProps.getValue(row) != 0
                                ? classes.textFieldColored
                                : classes.textField
                          }
                          value={editableProps.getValue(row)}
                          onChange={e => editableProps.onChange(e, row, i)}
                          onBlur={e => editableProps.onBlur(e, row, i)}
                          variant="outlined"
                          placeholder="E.g. 100 000,00"
                          type="number"
                          inputProps={{
                            style: {
                              textAlign: "right",
                              fontWeight:
                                  editableProps.getValue(row) != 0 ? "bold" : "normal",
                              backgroundColor:
                                  editableProps.getValue(row) != 0 ? "#FFF0F2" : "#fff",
                            },
                          }}
                          fullWidth
                      />
                  ) : headCell.id === "bio_mandate" ? (
                      row[headCell.id]
                  ) : headCell.id === "reduction_in_climate_emission" ? (
                      parseData(row[headCell.id], 2) + " %"
                  ) : parseDataDate.includes(headCell.id) ? (
                      row[headCell.id] ? moment(row[headCell.id]).format("YYYY-MM-DD") : null
                  ) : parseDataDecimal.includes(headCell.id) ? (
                      parseData(row[headCell.id], 2)
                  ) : (
                      parseData(row[headCell.id])
                  )}
                </StyledTableCell>
            )
        )}
      </StyledTableRow>
  );
}

function EnhancedTableRowPrevTable(props: {
  row: any;
  headCells: headCell[];
  classes: ReturnType<typeof useStyles>;
  filteredHead: any;
  smartProps: any;
  editableProps: any;
  open: any;
  setOpen: any;
  backgroundColor?: any;
}) {
  const {
    row,
    headCells,
    classes,
    filteredHead,
    smartProps,
    editableProps,
    open,
    setOpen,
    backgroundColor,
  } = props;

  return (
      <StyledTableRow
          style={{
            backgroundColor,
          }}
      >
        {headCells.map((headCell, i) =>
            filteredHead[headCell.id] ? null : (
                <StyledTableCell
                    align={headCell.alignRight ? "right" : "left"}
                    key={headCell.id + row.name + i}
                    style={{
                      whiteSpace: smartProps ? "nowrap" : "break-spaces",
                    }}
                >
                  {i === 0 && row.child ? (
                      <KeyboardArrowDownIcon
                          color="primary"
                          className={classes.toggleIcon}
                          style={{
                            transform: open ? "rotate(180deg)" : "rotate(0deg)",
                            color: open ? brandColors.primary.main : "#fff",
                            backgroundColor: open ? "#fff" : brandColors.primary.main,
                          }}
                          fontSize="small"
                          onClick={() => setOpen(!open)}
                      />
                  ) : editableProps && headCell.id === editableProps.id ? (
                      <TextField
                          className={
                            editableProps.getValue(row) != 0
                                ? classes.textFieldColored
                                : classes.textField
                          }
                          value={editableProps.getValue(row)}
                          onChange={e => editableProps.onChange(e, row, i)}
                          onBlur={e => editableProps.onBlur(e, row, i)}
                          variant="outlined"
                          placeholder="E.g. 100 000,00"
                          type="number"
                          inputProps={{
                            style: {
                              textAlign: "right",
                              fontWeight:
                                  editableProps.getValue(row) != 0 ? "bold" : "normal",
                              backgroundColor:
                                  editableProps.getValue(row) != 0 ? "#FFF0F2" : "#fff",
                            },
                          }}
                          fullWidth
                      />
                  ) : headCell.id === "reduction_in_climate_emission" ? (
                      parseData(row[headCell.id], 2) + " %"
                  ) : headCell.id === "available_quantity" ? (
                      row['allocated_quantity'] ? parseData(parseFloat(row['available_quantity']) - parseFloat(row['allocated_quantity'])) : parseData(parseFloat(row['available_quantity']))
                  ) : (
                      parseData(row[headCell.id])
                  )}
                </StyledTableCell>
            )
        )}
      </StyledTableRow>
  );
}

function EnhancedTableRowAllocation(props: {
  row: any;
  headCells: headCell[];
  classes: ReturnType<typeof useStyles>;
  filteredHead: any;
  smartProps: any;
  editableProps: any;
  open: any;
  setOpen: any;
  backgroundColor?: any;
}) {
  const {
    row,
    headCells,
    classes,
    filteredHead,
    smartProps,
    editableProps,
    open,
    setOpen,
    backgroundColor,
  } = props;
  return (
      <StyledTableRow
          style={{
            backgroundColor,
          }}
      >
        {headCells.map((headCell, i) =>
            filteredHead[headCell.id] ? null : (
                <StyledTableCell
                    align={headCell.alignRight ? "right" : "left"}
                    key={headCell.id + row.name + i}
                    style={{
                      whiteSpace: smartProps ? "nowrap" : "break-spaces",
                    }}
                >
                  {i === 0 && row.child ? (
                      <KeyboardArrowDownIcon
                          color="primary"
                          className={classes.toggleIcon}
                          style={{
                            transform: open ? "rotate(180deg)" : "rotate(0deg)",
                            color: open ? brandColors.primary.main : "#fff",
                            backgroundColor: open ? "#fff" : brandColors.primary.main,
                          }}
                          fontSize="small"
                          onClick={() => setOpen(!open)}
                      />
                  ) : editableProps && headCell.id === editableProps.id ? (
                      <TextField
                          className={
                            editableProps.getValue(row) != 0
                                ? classes.textFieldColored
                                : classes.textField
                          }
                          value={editableProps.getValue(row) != 0 ? editableProps.getValue(row) : ""}
                          onChange={e => editableProps.onChange(e, row, i)}
                          onBlur={e => editableProps.onBlur(e, row, i)}
                          variant="outlined"
                          placeholder="E.g. 100 000,00"
                          type="text"
                          inputProps={{
                            style: {
                              textAlign: "right",
                              fontWeight:
                                  editableProps.getValue(row) != 0 ? "bold" : "normal",
                              backgroundColor:
                                  editableProps.getValue(row) != 0 ? "#FFF0F2" : "#fff",
                            },
                          }}
                          fullWidth
                      />
                  ) : headCell.id === "reduction_in_climate_emission" ? (
                      parseAndFormat(row[headCell.id], 2) + " %"
                  ) : headCell.id === "available_quantity" ? (
                      editableProps ? (
                          parseToFloat(row['allocated_quantity']) > parseToFloat(row['available_quantity']) ? (
                              <span style={{fontWeight: 'bold', color: 'red'}}>
                          Volume exceeded
                        </span>
                          ) : (
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <button
                                    onClick={() =>
                                        editableProps.onChange(
                                            { target: { value: String(parseToFloat(row["available_quantity"])) } },
                                            row,
                                            i
                                        )
                                    }
                                    style={{
                                      background: "none",
                                      border: "none",
                                      paddingTop: "5px",
                                      paddingLeft: "0",
                                      cursor: "pointer",
                                    }}
                                    title="Use available quantity"
                                >
                                  <AddCircleIcon
                                      style={{
                                        color: "#000000",
                                        fontSize: "18px",
                                      }}
                                  />
                                </button>
                                <span>{parseAndFormat(parseToFloat(row["available_quantity"]), 2)}</span>
                              </div>
                          )
                      ) : parseAndFormat(parseToFloat(row['available_quantity']) - parseToFloat(row['allocated_quantity']), 2)
                  ) : headCell.id === "allocated_quantity" ? (
                      row['allocated_quantity'] ? parseAndFormat(parseToFloat(row['allocated_quantity']), 2) : 0
                  ) : (
                      parseAndFormat(row[headCell.id], 2)
                  )}
                </StyledTableCell>
            )
        )}
      </StyledTableRow>
  );
}

function EnhancedTableRow(props: {
  row: any;
  headCells: headCell[];
  classes: ReturnType<typeof useStyles>;
  filteredHead: any;
  smartProps: any;
  editableProps: any;
  open: any;
  setOpen: any;
  backgroundColor?: any;
}) {
  const {
    row,
    headCells,
    classes,
    filteredHead,
    smartProps,
    editableProps,
    open,
    setOpen,
    backgroundColor,
  } = props;

  return (
      <StyledTableRow
          style={{
            backgroundColor,
          }}
      >
        {headCells.map((headCell, i) =>
            filteredHead[headCell.id] ? null : (
                <StyledTableCell
                    align={headCell.alignRight ? "right" : "left"}
                    key={headCell.id + row.name + i}
                    style={{
                      whiteSpace: smartProps ? "nowrap" : "break-spaces",
                    }}
                >
                  {i === 0 && row.child ? (
                      <KeyboardArrowDownIcon
                          color="primary"
                          className={classes.toggleIcon}
                          style={{
                            transform: open ? "rotate(180deg)" : "rotate(0deg)",
                            color: open ? brandColors.primary.main : "#fff",
                            backgroundColor: open ? "#fff" : brandColors.primary.main,
                          }}
                          fontSize="small"
                          onClick={() => setOpen(!open)}
                      />
                  ) : editableProps && headCell.id === editableProps.id ? (
                      <TextField
                          className={
                            editableProps.getValue(row) != 0
                                ? classes.textFieldColored
                                : classes.textField
                          }
                          value={editableProps.getValue(row) != 0 ? editableProps.getValue(row) : ""}
                          onChange={e => editableProps.onChange(e, row, i)}
                          onBlur={e => editableProps.onBlur(e, row, i)}
                          variant="outlined"
                          placeholder="E.g. 100 000,00"
                          type="text"
                          inputProps={{
                            style: {
                              textAlign: "right",
                              fontWeight:
                                  editableProps.getValue(row) != 0 ? "bold" : "normal",
                              backgroundColor:
                                  editableProps.getValue(row) != 0 ? "#FFF0F2" : "#fff",
                            },
                          }}
                          fullWidth
                      />
                  ) : headCell.id === "reduction_in_climate_emission" ? (
                      parseAndFormat(row[headCell.id], 2) + " %"
                  ) : headCell.id === "available_quantity" ? (
                      parseToFloat(row['allocated_quantity']) > parseToFloat(row['available_quantity']) ? (
                          <span style={{fontWeight: 'bold', color: 'red'}}>
                    Volume exceeded
                  </span>
                      ) : (
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <button
                                onClick={() =>
                                    editableProps.onChange(
                                        { target: { value: String(parseToFloat(row["available_quantity"])) } },
                                        row,
                                        i
                                    )
                                }
                                style={{
                                  background: "none",
                                  border: "none",
                                  paddingTop: "5px",
                                  paddingLeft: "0",
                                  cursor: "pointer",
                                }}
                                title="Use available quantity"
                            >
                              <AddCircleIcon
                                  style={{
                                    color: "#000000",
                                    fontSize: "18px",
                                  }}
                              />
                            </button>
                            <span>{parseAndFormat(parseToFloat(row['available_quantity']), 2)}</span>
                          </div>
                      )
                  ) : (
                      parseAndFormat(row[headCell.id], 2)
                  )}
                </StyledTableCell>
            )
        )}
      </StyledTableRow>
  );
}

function ExtendableTableRowStorage(props: {
  index: number;
  row: any;
  headCells: headCell[];
  classes: ReturnType<typeof useStyles>;
  filteredHead: any;
  smartProps: any;
  editableProps: any;
  extendedRows: any;
  handleSetExtendedRow: any;
}) {
  const {
    index,
    row,
    headCells,
    classes,
    filteredHead,
    smartProps,
    editableProps,
    extendedRows,
    handleSetExtendedRow,
  } = props;

  const open = extendedRows[index];

  const getAlternatingColor = (idx: number) => {
    return idx % 2 === 0 ? "#F2F5F7" : "#fff";
  };

  return (
      <React.Fragment>
        {row.child.length > 1 && (
            <EnhancedTableRowStorage
                key={index + "_enhancedTableRow"}
                row={row}
                headCells={headCells}
                classes={classes}
                filteredHead={filteredHead}
                smartProps={smartProps}
                editableProps={editableProps}
                open={open}
                setOpen={() => handleSetExtendedRow(index)}
                backgroundColor={open ? "#ffdce1" : getAlternatingColor(index)}
            />
        )}
        {row.child.length > 1 && open && (
            <React.Fragment>
              {row.child &&
                  row.child.map((sub_row: any, i: number) => (
                      <EnhancedTableRowStorage
                          key={index + "_" + row.id + "sub_row" + i}
                          row={sub_row}
                          headCells={headCells}
                          classes={classes}
                          filteredHead={filteredHead}
                          smartProps={smartProps}
                          editableProps={editableProps}
                          open={open}
                          setOpen={() => handleSetExtendedRow(index)}
                          backgroundColor={open ? "#FFF0F2" : getAlternatingColor(index)}
                      />
                  ))}
              <TableRow></TableRow>
            </React.Fragment>
        )}

        {row.child.length == 1 && (
            <React.Fragment>
              {row.child &&
                  row.child.map((sub_row: any, i: number) => (
                      <EnhancedTableRowStorage
                          key={index + "_" + row.id + "sub_row" + i}
                          row={sub_row}
                          headCells={headCells}
                          classes={classes}
                          filteredHead={filteredHead}
                          smartProps={smartProps}
                          editableProps={editableProps}
                          open={true}
                          setOpen={() => handleSetExtendedRow(index)}
                          backgroundColor={getAlternatingColor(index)}
                      />
                  ))}
              <TableRow></TableRow>
            </React.Fragment>
        )}
      </React.Fragment>
  );
}

function ExtendableTableRow(props: {
  index: number;
  row: any;
  headCells: headCell[];
  classes: ReturnType<typeof useStyles>;
  filteredHead: any;
  smartProps: any;
  editableProps: any;
  extendedRows: any;
  handleSetExtendedRow: any;
  config: SmartTableConfig;
}) {
  const {
    index,
    row,
    headCells,
    classes,
    filteredHead,
    smartProps,
    editableProps,
    extendedRows,
    handleSetExtendedRow,
    config,
  } = props;

  const getAlternatingColor = (idx: number) => {
    return idx % 2 === 0 ? "#F2F5F7" : "#fff";
  };

  const open = extendedRows[index];
  const EnhancedTableRowComponent: EnhancedTableRowComponentType =
    config.useForAllocation
      ? EnhancedTableRowAllocation
      : config.useForPrevTable
      ? EnhancedTableRowPrevTable
      : EnhancedTableRow;

  return (
    <React.Fragment>
        <EnhancedTableRowComponent
            key={index + "_enhancedTableRow"}
            row={row}
            headCells={headCells}
            classes={classes}
            filteredHead={filteredHead}
            smartProps={smartProps}
            editableProps={editableProps}
            open={open}
            setOpen={() => handleSetExtendedRow(index)}
            backgroundColor={open ? "#ffdce1" : getAlternatingColor(index)}
        />
        {open && (
          <React.Fragment>
            {row.child &&
              row.child.map((sub_row: any, i: number) => (
                <EnhancedTableRowComponent
                    key={index + "_" + row.id + "sub_row" + i}
                    row={sub_row}
                    headCells={headCells}
                    classes={classes}
                    filteredHead={filteredHead}
                    smartProps={smartProps}
                    editableProps={editableProps}
                    open={open}
                    setOpen={() => handleSetExtendedRow(index)}
                    backgroundColor={open ? "#FFF0F2" : getAlternatingColor(index)}
                />
              ))}
            <TableRow></TableRow>
          </React.Fragment>
        )}
    </React.Fragment>
  );
}

const SmartTable: React.FC<ISmartTableProps> = ({
  headCells,
  smartProps,
  editableProps,
  whiteHeader,
  verticalOverflow,
  rows,
  aggregate,
  config = {},
}) => {
  const classes = useStyles();
  const [extendedRows, setExtendedRows] = React.useState<{
    [index: number]: any;
  }>({});
  const [filteredHead, setFilteredHead] = React.useState(
    smartProps
      ? {
        ...smartProps.initFilteredHead,
      }
      : {}
  );
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [anchorElRow, setAnchorElRow] = React.useState<null | HTMLElement>(
    null
  );
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState(headCells[0].id);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(
    verticalOverflow ? 1000000 : 10
  );

  const handleSetExtendedRow = (index: number): void => {
    if (!extendedRows[index]) {
      setExtendedRows({ ...extendedRows, [index]: true });
    }
    else { setExtendedRows({ ...extendedRows, [index]: null }); }
  };

  const handleClickHead = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClickRow = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: any
  ) => {
    smartProps.selectEntry(row);
    setAnchorElRow(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setAnchorElRow(null);
  };

  const handleChangeFilter = (name: string) => {
    setFilteredHead({ ...filteredHead, [name]: !filteredHead[name] });
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const aggregateConditions = (aggregate: string[], item1: any, item2: any) => {
    if (!item1 || !item2) { return false; }
    for (let index = 0; index < aggregate.length; index++) {
      if (item1[aggregate[index]] !== item2[aggregate[index]]) { return false; }
    }
    return true;
  };

  const getAggregatedRow = (
    rows: any,
    aggregate: { required: string[]; addUp: string[] }
  ): { [x: string]: any } => {
    let aggregatedRow: { [x: string]: any } = {};
    aggregate.required.map(attribute => {
      aggregatedRow[attribute] = rows[0][attribute];
    });
    aggregate.addUp.map(attribute => {
      let total = 0;
      rows.map((row: any) => (total = total + row[attribute]));
      aggregatedRow[attribute] = total;
    });
    return aggregatedRow;
  };

  const rowsList = Array.isArray(rows)
    ? rows
    : rows != null && typeof rows === "object"
      ? Object.values(rows)
      : [];

  let newRows: any[] = [];
  if (aggregate && aggregate.required.length > 0 && rowsList.length > 0) {
    let copyRows = [...rowsList];
    for (let index = 0; index < copyRows.length; index++) {
      if (!copyRows[index].filteredOut) {
        let groupedRows = copyRows.filter((row: any, i: number) => {
          if (aggregateConditions(aggregate.required, copyRows[index], row)) {
            copyRows[i] = { ...row, filteredOut: true };
            return true;
          }
          return false;
        });
        let newRow: { [x: string]: any } = {
          ...getAggregatedRow(groupedRows, aggregate),
          child: groupedRows,
        };
        if (aggregate.removeZeroBalanced && newRow.quantity == 0) {
        } else if (aggregate.showZeroBalanced && newRow.quantity != 0) {
        } else { newRows.push(newRow); }
      }
    }
  } else {
    newRows = rowsList;
  }

  if (!Array.isArray(newRows) || newRows.length === 0) {
    return (
      <div className={classes.noData}>
        <Typography variant="body1" className={classes.noDataText}>
          No data
        </Typography>
      </div>
    );
  } else {
    return (
      <div className={classes.root}>
        <div className={classes.content}>
          {smartProps && <div className={classes.sideGradient}></div>}
          <div
            className={
              smartProps ? classes.tableWrapperSmart : classes.tableWrapper
            }
          >
            <Table
              className={classes.table}
              aria-labelledby="tableTitle"
              size={"medium"}
              aria-label="enhanced table"
            >
              <EnhancedTableHead
                backgroundColor={
                  smartProps || whiteHeader
                    ? "white"
                    : "#FFF0F2"
                }
                headHeight={smartProps ? "75px" : "30px"}
                classes={classes}
                order={order}
                orderBy={orderBy}
                headCells={headCells}
                filteredHead={filteredHead}
                onRequestSort={handleRequestSort}
              />

              {newRows.length === 0 ? null : (
                <TableBody>
                  {stableSort(newRows, getSorting(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      return (
                          <>
                            {config.useForStorage ? (
                                <ExtendableTableRowStorage
                                    key={index + "_" + row.id}
                                    index={index}
                                    row={row}
                                    headCells={headCells}
                                    classes={classes}
                                    filteredHead={filteredHead}
                                    smartProps={smartProps}
                                    editableProps={editableProps}
                                    extendedRows={extendedRows}
                                    handleSetExtendedRow={handleSetExtendedRow}
                                />
                            ) : (
                                <ExtendableTableRow
                                    key={index + "_" + row.id}
                                    index={index}
                                    row={row}
                                    headCells={headCells}
                                    classes={classes}
                                    filteredHead={filteredHead}
                                    smartProps={smartProps}
                                    editableProps={editableProps}
                                    extendedRows={extendedRows}
                                    handleSetExtendedRow={handleSetExtendedRow}
                                    config={config}
                                />
                            )}
                          </>
                      );
                    })}
                </TableBody>
              )}
            </Table>
          </div>
          {smartProps ? (
            <div className={classes.sideButtons}>
              <IconButton
                className={classes.sideButtonHead}
                onClick={handleClickHead}
              >
                <MoreHorizIcon />
              </IconButton>
              {stableSort(newRows, getSorting(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  if (row.child) {
                    let buttons = null;
                    if (extendedRows[index]) {
                      buttons = (
                        <React.Fragment>
                          <IconButton
                            key={"iconButtonEmptyExtended" + index}
                            className={classes.sideButton}
                          ></IconButton>
                          {Array.isArray(row.child) &&
                            row.child.map((sub_row: any, i: number) => (
                              <IconButton
                                key={index + "iconButtonChild" + i}
                                className={classes.sideButton}
                                onClick={e => handleClickRow(e, sub_row)}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            ))}
                        </React.Fragment>
                      );
                    }
                    else {
                      buttons = (
                        <IconButton
                          key={"iconButtonEmpty" + index}
                          className={classes.sideButton}
                        ></IconButton>
                      );
                    }
                    return buttons;
                  } else {
                    return (
                      <IconButton
                        key={"iconButton" + index}
                        className={classes.sideButton}
                        onClick={e => handleClickRow(e, row)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    );
                  }
                })}
              <StyledHeadMenu
                id="simple-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <div className={classes.menu}>
                  <Typography className={classes.menuTitle}>
                    Manage Columns:
                  </Typography>
                  {headCells.map((head, index) => (
                    <FormControlLabel
                      key={head.id + "MenuItem" + index}
                      checked={!filteredHead[head.id]}
                      value={head.id}
                      control={<Radio color="primary" />}
                      label={
                        <Typography
                          className={
                            filteredHead[head.id]
                              ? classes.menuLabel
                              : classes.menuLabelSelected
                          }
                        >
                          {head.idFormattedName ? (
                            <FormattedMessage id={head.idFormattedName} />
                          ) : (
                            head.name
                          )}
                        </Typography>
                      }
                      onClick={e => handleChangeFilter(head.id)}
                    />
                  ))}
                </div>
              </StyledHeadMenu>
              <StyledRowMenu
                id="simple-menu"
                anchorEl={anchorElRow}
                keepMounted
                open={Boolean(anchorElRow)}
                onClose={handleClose}
              >
                <MenuList>
                  <StyledMenuItem
                    onClick={row => {
                      smartProps.editEntry(row);
                      handleClose();
                    }}
                    dense
                    disabled={smartProps.disabledActions}
                  >
                    <ListItemIcon>
                      <EditOutlinedIcon fontSize="default" />
                    </ListItemIcon>
                    <Typography variant="body2" color="primary">
                      Edit
                    </Typography>
                  </StyledMenuItem>
                  <StyledMenuItem
                    onClick={row => {
                      smartProps.deleteEntry(row);
                      handleClose();
                    }}
                    dense
                    disabled={smartProps.disabledActions}
                  >
                    <ListItemIcon>
                      <DeleteOutlinedIcon fontSize="default" color="error" />
                    </ListItemIcon>
                    <Typography variant="body2" color="error">
                      Delete
                    </Typography>
                  </StyledMenuItem>
                </MenuList>
              </StyledRowMenu>
            </div>
          ) : null}
        </div>
        {!verticalOverflow && (
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={newRows ? newRows.length : 0}
            rowsPerPage={rowsPerPage}
            page={page}
            backIconButtonProps={{
              "aria-label": "previous page",
            }}
            nextIconButtonProps={{
              "aria-label": "next page",
            }}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        )}
      </div>
    );
  }
};

export default SmartTable;
